import { useState, useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import { ApiError } from "@njbsoft/api-client";
import { adminApiClient } from "@/lib/auth";
import { adminKeys } from "@/hooks/use-admin-keys";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { StatCard, StatCardSkeleton } from "@/components/stat-card";

type OrgCustomCount = AdminComponents["schemas"]["OrgCustomCount"];
type AdminSystemParameter = AdminComponents["schemas"]["AdminSystemParameter"];

const PAGE_SIZE = 20;

export const Route = createFileRoute("/_admin/parameters/")({
  component: AdminParametersPage,
});

function useAdminParameterStats() {
  return useQuery({
    queryKey: adminKeys.parameterStats(),
    queryFn: async () => {
      const { data, error, response } = await adminApiClient.GET("/parameters/stats");
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to fetch parameter stats";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    staleTime: 60_000,
  });
}

function useAdminParameters(params: {
  search: string;
  category: string;
  limit: number;
  offset: number;
}) {
  return useQuery({
    queryKey: adminKeys.parameters(params),
    queryFn: async () => {
      const { data, error, response } = await adminApiClient.GET("/parameters", {
        params: {
          query: {
            search: params.search || undefined,
            category: params.category || undefined,
            limit: params.limit,
            offset: params.offset,
          },
        },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to fetch parameters";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    staleTime: 60_000,
  });
}

const orgColHelper = createColumnHelper<OrgCustomCount>();
const sysColHelper = createColumnHelper<AdminSystemParameter>();

function AdminParametersPage() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);

  const { data: statsData, isLoading: statsLoading } = useAdminParameterStats();
  const { data: paramsData, isLoading: paramsLoading } = useAdminParameters({
    search,
    category,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const orgColumns = useMemo(
    () => [
      orgColHelper.accessor("org_name", {
        header: "Organization",
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Link
              to="/parameters/orgs/$orgId"
              params={{ orgId: row.org_id }}
              className="font-medium text-blue-600 hover:underline"
            >
              {info.getValue()}
            </Link>
          );
        },
      }),
      orgColHelper.accessor("custom_count", {
        header: "Custom Parameters",
        enableSorting: false,
        cell: (info) => <span className="font-mono text-sm">{info.getValue()}</span>,
      }),
    ],
    [],
  );

  const sysColumns = useMemo(
    () => [
      sysColHelper.accessor("code", {
        header: "Code",
        enableSorting: false,
        cell: (info) => <span className="font-mono text-sm">{info.getValue()}</span>,
      }),
      sysColHelper.accessor("name", {
        header: "Name",
        enableSorting: false,
      }),
      sysColHelper.accessor("category", {
        header: "Category",
        enableSorting: false,
        cell: (info) => (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
            {info.getValue()}
          </span>
        ),
      }),
      sysColHelper.accessor("cas_number", {
        header: "CAS Number",
        enableSorting: false,
        cell: (info) => <span className="text-sm text-slate-500">{info.getValue() ?? "-"}</span>,
      }),
      sysColHelper.accessor("epa_storet_code", {
        header: "EPA STORET Code",
        enableSorting: false,
        cell: (info) => (
          <span className="font-mono text-sm text-slate-500">{info.getValue() ?? "-"}</span>
        ),
      }),
    ],
    [],
  );

  const categories = useMemo(() => {
    const items = paramsData?.data ?? [];
    const unique = new Set(items.map((p) => p.category));
    return Array.from(unique).sort();
  }, [paramsData]);

  const total = paramsData?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Parameters</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {statsLoading ? (
          <StatCardSkeleton />
        ) : (
          <div role="group" aria-label="System Parameters">
            <StatCard
              title="System Parameters"
              value={statsData?.system_count ?? 0}
              subtitle="Seeded reference parameters"
            />
          </div>
        )}
      </div>

      {/* Per-org custom counts */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-slate-800">
          Custom Parameters by Organization
        </h2>
        <DataTable
          columns={orgColumns}
          data={statsData?.org_custom_counts ?? []}
          isLoading={statsLoading}
          emptyMessage="No organizations have custom parameters yet"
        />
      </div>

      {/* System parameters table */}
      <div>
        <h2 className="mb-3 text-lg font-semibold text-slate-800">System Parameters</h2>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search parameters..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-64 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400"
          />
          <select
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900"
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <DataTable
          columns={sysColumns}
          data={paramsData?.data ?? []}
          sorting={sorting}
          onSortingChange={setSorting}
          isLoading={paramsLoading}
          emptyMessage="No parameters found"
        />
        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
